/**
 * @module bridges
 * @package @geenius/adapters/react-chakra
 * @description Chakra UI bridge components for async adapter loading, error,
 * and notification surfaces. Adapter contracts stay delegated to the React
 * reference variant.
 */

"use client";

import {
  Alert,
  Box,
  Button,
  createToaster,
  Spinner,
  Stack,
  Text,
  Toast,
  Toaster,
} from "@chakra-ui/react";
import type { LoggerAdapter, LogMeta } from "@geenius/adapters";
import {
  Component,
  type ErrorInfo,
  type JSX,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
} from "react";

type BridgeSize = "sm" | "md" | "lg";

export type ToastBridgeStatus =
  | "default"
  | "info"
  | "success"
  | "warning"
  | "error"
  | "loading";

export type ToastBridgePlacement =
  | "top-start"
  | "top"
  | "top-end"
  | "bottom-start"
  | "bottom"
  | "bottom-end";

const spinnerSizes = {
  sm: "sm",
  md: "md",
  lg: "lg",
} as const satisfies Record<BridgeSize, "sm" | "md" | "lg">;

const loadingPadding: Record<BridgeSize, string> = {
  sm: "3",
  md: "4",
  lg: "5",
};

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(typeof error === "string" ? error : "Adapter bridge error");
}

function nodeText(value: ReactNode, fallback: string): string {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return fallback;
}

function toastType(
  status: ToastBridgeStatus,
): Exclude<ToastBridgeStatus, "default"> {
  return status === "default" ? "info" : status;
}

function toastDuration(duration: number): number {
  return duration === 0 ? Infinity : duration;
}

export interface LoadingBridgeProps {
  /** Controls whether the loading affordance renders. Defaults to true. */
  loading?: boolean;
  /** Alias for `loading` used by async adapter state objects. */
  isLoading?: boolean;
  /** Main loading label announced to assistive technology. */
  label?: ReactNode;
  /** Optional supporting copy rendered below the label. */
  description?: ReactNode;
  /** Visual density of the Chakra spinner and container. */
  size?: BridgeSize;
  /** Content rendered when loading is explicitly false. */
  children?: ReactNode;
  /** Optional class name passed to the Chakra wrapper. */
  className?: string;
}

/** Renders a Chakra loading affordance for async adapter work. */
export function LoadingBridge({
  children,
  className,
  description,
  isLoading,
  label = "Loading adapters",
  loading,
  size = "md",
}: LoadingBridgeProps): JSX.Element {
  const active = isLoading ?? loading ?? true;

  if (!active) {
    return <>{children}</>;
  }

  return (
    <Box
      aria-busy="true"
      aria-live="polite"
      borderColor="border"
      borderRadius="md"
      borderWidth="1px"
      className={className}
      padding={loadingPadding[size]}
      role="status"
    >
      <Stack align="center" direction="row" gap="3">
        <Spinner aria-hidden="true" size={spinnerSizes[size]} />
        <Stack gap="1" minWidth="0">
          <Text as="span" fontWeight="medium">
            {label}
          </Text>
          {description ? (
            <Text as="span" color="fg.muted" textStyle="sm">
              {description}
            </Text>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}

type ErrorBoundaryFallback =
  | ReactNode
  | ((error: Error, reset: () => void) => ReactNode);

export interface ErrorBoundaryBridgeProps {
  /** React subtree protected by the boundary. */
  children?: ReactNode;
  /** Optional controlled async adapter error. */
  error?: unknown;
  /** Optional custom fallback node or renderer. */
  fallback?: ErrorBoundaryFallback;
  /** Heading used by the default Chakra fallback. */
  title?: ReactNode;
  /** Supporting copy used by the default fallback. */
  description?: ReactNode;
  /** Label for the default reset action. */
  resetLabel?: ReactNode;
  /** Additional class name passed to the default alert. */
  className?: string;
  /** Called when a descendant throws. */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Called before local error state is reset. */
  onReset?: () => void;
}

interface ErrorBoundaryBridgeState {
  caughtError: Error | null;
}

/** Catches React render failures and displays a Chakra adapter error state. */
export class ErrorBoundaryBridge extends Component<
  ErrorBoundaryBridgeProps,
  ErrorBoundaryBridgeState
> {
  state: ErrorBoundaryBridgeState = { caughtError: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryBridgeState {
    return { caughtError: normalizeError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.props.onError?.(normalizeError(error), errorInfo);
  }

  private reset = (): void => {
    this.props.onReset?.();
    this.setState({ caughtError: null });
  };

  render(): ReactNode {
    const error =
      this.props.error !== undefined
        ? normalizeError(this.props.error)
        : this.state.caughtError;

    if (!error) {
      return this.props.children;
    }

    if (typeof this.props.fallback === "function") {
      return this.props.fallback(error, this.reset);
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <Alert.Root
        className={this.props.className}
        role="alert"
        status="error"
        variant="subtle"
      >
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>{this.props.title ?? "Adapter error"}</Alert.Title>
          {this.props.description ? (
            <Alert.Description>{this.props.description}</Alert.Description>
          ) : null}
          <Text as="span" wordBreak="break-word">
            {error.message}
          </Text>
          <Button
            alignSelf="flex-start"
            marginTop="3"
            onClick={this.reset}
            size="sm"
            type="button"
            variant="outline"
          >
            {this.props.resetLabel ?? "Retry"}
          </Button>
        </Alert.Content>
      </Alert.Root>
    );
  }
}

export interface ToastBridgeProps {
  /** Optional subtree rendered inside the Chakra toaster host. */
  children?: ReactNode;
  /** Controls whether the declarative toast should be emitted. */
  open?: boolean;
  /** Toast title. */
  title?: ReactNode;
  /** Toast message alias used by some adapter telemetry states. */
  message?: ReactNode;
  /** Optional supporting toast message. */
  description?: ReactNode;
  /** Chakra toast status. */
  status?: ToastBridgeStatus;
  /** Alias for `status` to match the other React library variants. */
  variant?: ToastBridgeStatus;
  /** Stable dedupe key for repeated declarative renders. */
  toastKey?: string;
  /** Auto-dismiss timeout in milliseconds; 0 keeps the toast visible. */
  duration?: number;
  /** Chakra toast placement. */
  placement?: ToastBridgePlacement;
  /** Optional action label rendered by the Chakra toast. */
  actionLabel?: ReactNode;
  /** Called when the optional action is pressed. */
  onAction?: () => void;
  /** Called when Chakra reports that the toast is no longer visible. */
  onDismiss?: () => void;
  /** Receives the Chakra toast id after emission. */
  onShown?: (toastId: string) => void;
  /** Optional logger adapter that receives one structured event per visible toast. */
  logger?: LoggerAdapter;
  /** Optional structured metadata merged into the logger event. */
  meta?: LogMeta;
  /** Optional class name passed to the Chakra toaster host. */
  className?: string;
}

/** Provides Chakra toast rendering and emits declarative adapter notifications. */
export function ToastBridge({
  actionLabel,
  children,
  className,
  description,
  duration = 0,
  logger,
  message,
  meta,
  onAction,
  onDismiss,
  onShown,
  open = true,
  placement = "bottom-end",
  status,
  title,
  toastKey,
  variant,
}: ToastBridgeProps): JSX.Element {
  const emittedKeyRef = useRef<string | null>(null);
  const tone = variant ?? status ?? "info";
  const type = toastType(tone);
  const toaster = useMemo(
    () =>
      createToaster({
        duration: toastDuration(duration),
        placement,
      }),
    [duration, placement],
  );
  const dedupeKey = useMemo(
    () =>
      toastKey ??
      [
        type,
        nodeText(title ?? message, "Adapter notification"),
        nodeText(description, ""),
      ].join(":"),
    [description, message, title, toastKey, type],
  );

  useEffect(() => {
    if (!open) {
      emittedKeyRef.current = null;
      return;
    }

    if (emittedKeyRef.current === dedupeKey) {
      return;
    }

    emittedKeyRef.current = dedupeKey;
    const titleText = nodeText(title ?? message, "Adapter notification");
    const descriptionText =
      typeof description === "string" ? description : undefined;
    const action =
      actionLabel && onAction
        ? {
            label: nodeText(actionLabel, "Open"),
            onClick: onAction,
          }
        : undefined;
    const toastId = toaster.create({
      action,
      closable: true,
      description,
      duration: toastDuration(duration),
      id: toastKey ?? dedupeKey,
      onStatusChange(details) {
        if (details.status === "unmounted" || details.status === "dismissing") {
          onDismiss?.();
        }
      },
      title: title ?? message ?? "Adapter notification",
      type,
    });

    onShown?.(toastId);

    void logger?.info(titleText, {
      bridge: "react-chakra",
      description: descriptionText,
      variant: tone,
      ...meta,
    });
  }, [
    actionLabel,
    dedupeKey,
    description,
    duration,
    logger,
    message,
    meta,
    onAction,
    onDismiss,
    onShown,
    open,
    title,
    toastKey,
    toaster,
    tone,
    type,
  ]);

  return (
    <>
      <Toaster className={className} toaster={toaster}>
        {(toast) => (
          <Toast.Root>
            <Toast.Indicator />
            <Stack gap="1" flex="1">
              {toast.title ? <Toast.Title>{toast.title}</Toast.Title> : null}
              {toast.description ? (
                <Toast.Description>{toast.description}</Toast.Description>
              ) : null}
            </Stack>
            {toast.action ? (
              <Toast.ActionTrigger>{toast.action.label}</Toast.ActionTrigger>
            ) : null}
            <Toast.CloseTrigger aria-label="Dismiss notification" />
          </Toast.Root>
        )}
      </Toaster>
      {children}
    </>
  );
}
